"use client"

import { useEffect, useState } from "react"
import { AlertTriangle, Radio } from "lucide-react"
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card"
import { Badge } from "@/components/ui/badge"
import {
  DEMO_ENGINES,
  DEMO_TODAY,
  DEMO_SITE,
  fmtTime,
  type EngineRun,
} from "@/lib/demo-data"

// Full 24h view: 0-24 hours
const DAY_MINUTES = 24 * 60

function barPosition(run: EngineRun) {
  const left = (Math.max(run.start, 0) / DAY_MINUTES) * 100
  const end = Math.min(run.end, DAY_MINUTES)
  const width = ((end - Math.max(run.start, 0)) / DAY_MINUTES) * 100
  return { left: `${left}%`, width: `${Math.max(width, 0.3)}%` }
}

function durationStr(start: number, end: number) {
  const d = end - start
  const h = Math.floor(d / 60)
  const m = d % 60
  return h > 0 ? `${h}h ${m}m` : `${m}m`
}

// Simulated "now" at 2:22 PM Guyana time (14:22 = 862 minutes)
const SIMULATED_NOW = 14 * 60 + 22

export default function DemoLivePage() {
  const [now, setNow] = useState(SIMULATED_NOW)

  // Tick the clock forward every 30 seconds (demo simulation)
  useEffect(() => {
    const interval = setInterval(() => {
      setNow((prev) => prev + 1)
    }, 30000)
    return () => clearInterval(interval)
  }, [])

  const nowPercent = (now / DAY_MINUTES) * 100
  const runs = DEMO_TODAY?.runs || []

  // Which engines are currently running?
  const currentlyRunning = DEMO_ENGINES.map((engine) => {
    const activeRun = runs.find(
      (r) => r.engineId === engine.engineId && !r.anomaly && r.start <= now && r.end >= now
    )
    const lastRun = runs
      .filter((r) => r.engineId === engine.engineId && !r.anomaly)
      .sort((a, b) => b.end - a.end)[0]
    return {
      ...engine,
      isRunning: !!activeRun,
      currentRun: activeRun,
      lastRun,
    }
  })

  const runningCount = currentlyRunning.filter((e) => e.isRunning).length

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">Live Monitoring</h1>
          <p className="text-sm text-muted-foreground">
            {DEMO_SITE.name} · {DEMO_TODAY?.weekday}, {DEMO_TODAY?.date}
          </p>
        </div>
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <Radio className="h-4 w-4 text-green-500 animate-pulse" />
            <span className="tabular-nums font-mono">{fmtTime(now)}</span>
          </div>
          <Badge variant="outline">{runningCount} / {DEMO_ENGINES.length} running</Badge>
        </div>
      </div>

      {/* Engine status cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {currentlyRunning.map((engine) => (
          <Card key={engine.id} className={engine.isRunning ? "border-green-500/20" : ""}>
            <CardHeader className="pb-2">
              <div className="flex items-center justify-between">
                <CardTitle className="text-sm font-medium">{engine.name}</CardTitle>
                <Badge variant={engine.isRunning ? "default" : "secondary"} className="text-[10px]">
                  {engine.isRunning ? "RUNNING" : "OFF"}
                </Badge>
              </div>
              <CardDescription>{engine.engineType}</CardDescription>
            </CardHeader>
            <CardContent>
              {engine.isRunning && engine.currentRun ? (
                <div className="text-sm">
                  <span className="text-muted-foreground">Since </span>
                  <span className="font-mono tabular-nums">{fmtTime(engine.currentRun.start)}</span>
                  <span className="text-muted-foreground"> · </span>
                  <span className="font-mono tabular-nums">{durationStr(engine.currentRun.start, now)}</span>
                </div>
              ) : engine.lastRun ? (
                <div className="text-sm text-muted-foreground">
                  Last: {fmtTime(engine.lastRun.start)} – {fmtTime(engine.lastRun.end)}
                </div>
              ) : (
                <div className="text-sm text-muted-foreground">No activity today</div>
              )}
            </CardContent>
          </Card>
        ))}
      </div>

      {/* 24h Timeline */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Today&apos;s Timeline</CardTitle>
          <CardDescription>24-hour view · Current time: {fmtTime(now)}</CardDescription>
        </CardHeader>
        <CardContent>
          {/* Hour labels */}
          <div className="relative ml-28 sm:ml-32 mr-4 h-5 mb-2">
            {[0, 3, 6, 9, 12, 15, 18, 21, 24].map((h) => {
              const pos = (h / 24) * 100
              const label = h === 0 ? "12a" : h === 12 ? "12p" : h === 24 ? "12a" : h < 12 ? `${h}a` : `${h - 12}p`
              return (
                <span
                  key={h}
                  className="absolute text-[10px] text-muted-foreground/40 -translate-x-1/2"
                  style={{ left: `${pos}%` }}
                >
                  {label}
                </span>
              )
            })}
          </div>

          {/* Engine tracks */}
          <div className="space-y-3">
            {DEMO_ENGINES.map((engine) => {
              const engineRuns = runs.filter((r) => r.engineId === engine.engineId)
              const normalRuns = engineRuns.filter((r) => !r.anomaly)
              const anomalyRuns = engineRuns.filter((r) => r.anomaly)

              return (
                <div key={engine.engineId} className="flex items-center gap-3">
                  <div className="w-28 sm:w-32 shrink-0 text-right pr-2">
                    <span className="text-xs text-muted-foreground">{engine.name}</span>
                  </div>
                  <div className="relative flex-1 h-8 bg-muted/[0.04] rounded border border-border/10 overflow-hidden">
                    {/* Work hours background (6am-5pm) */}
                    <div
                      className="absolute inset-y-0 bg-muted/[0.06]"
                      style={{
                        left: `${(6 / 24) * 100}%`,
                        width: `${(11 / 24) * 100}%`,
                      }}
                    />

                    {/* Normal run bars */}
                    {normalRuns.map((run, i) => (
                      <div
                        key={i}
                        className="absolute inset-y-1 rounded-sm bg-foreground/60 group cursor-default"
                        style={barPosition(run)}
                      >
                        <div className="hidden group-hover:block absolute -top-8 left-1/2 -translate-x-1/2 bg-popover border border-border rounded px-2 py-1 text-[10px] whitespace-nowrap z-10 shadow-lg">
                          {fmtTime(run.start)} – {fmtTime(run.end)} ({durationStr(run.start, run.end)})
                        </div>
                      </div>
                    ))}

                    {/* Anomaly bars (red) */}
                    {anomalyRuns.map((run, i) => (
                      <div
                        key={`a-${i}`}
                        className="absolute inset-y-1 rounded-sm bg-red-500/60 group cursor-default"
                        style={barPosition(run)}
                      >
                        <div className="hidden group-hover:block absolute -top-8 left-1/2 -translate-x-1/2 bg-popover border border-border rounded px-2 py-1 text-[10px] whitespace-nowrap z-10 shadow-lg text-red-400">
                          {fmtTime(run.start)} – {fmtTime(Math.min(run.end, DAY_MINUTES))} (unscheduled)
                        </div>
                      </div>
                    ))}

                    {/* Current time marker */}
                    <div
                      className="absolute inset-y-0 w-px bg-green-500 z-10"
                      style={{ left: `${nowPercent}%` }}
                    >
                      <div className="absolute -top-1 -translate-x-1/2 h-2 w-2 rounded-full bg-green-500" />
                    </div>
                  </div>

                  {/* Status */}
                  <div className="w-12 shrink-0">
                    {currentlyRunning.find((e) => e.engineId === engine.engineId)?.isRunning ? (
                      <span className="text-[10px] font-medium text-green-500">ON</span>
                    ) : (
                      <span className="text-[10px] text-muted-foreground/40">OFF</span>
                    )}
                  </div>
                </div>
              )
            })}
          </div>

          {/* Legend */}
          <div className="flex items-center gap-5 mt-4 pt-3 border-t text-[10px] text-muted-foreground/50 ml-28 sm:ml-32">
            <div className="flex items-center gap-1.5">
              <div className="h-2.5 w-5 rounded-sm bg-muted/[0.06] border border-border/10" />
              Work hours (6a–5p)
            </div>
            <div className="flex items-center gap-1.5">
              <div className="h-2.5 w-5 rounded-sm bg-foreground/60" />
              Engine running
            </div>
            <div className="flex items-center gap-1.5">
              <div className="h-2.5 w-5 rounded-sm bg-red-500/60" />
              Unscheduled
            </div>
            <div className="flex items-center gap-1.5">
              <div className="h-px w-5 bg-green-500" />
              Now
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Anomaly alert if present */}
      {runs.some((r) => r.anomaly) && (
        <Card className="border-red-500/20">
          <CardContent className="flex items-start gap-3 pt-6">
            <AlertTriangle className="h-4 w-4 text-red-400 mt-0.5 shrink-0" />
            <div>
              <p className="text-sm font-medium">After-hours activity detected</p>
              {runs.filter((r) => r.anomaly).map((run, i) => (
                <p key={i} className="text-xs text-muted-foreground mt-1">
                  {run.engineName}: {fmtTime(run.start)} – {fmtTime(Math.min(run.end, DAY_MINUTES))} ({durationStr(run.start, run.end)})
                </p>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  )
}
